import { resolveUrl,api } from '@/lib/api-client'
import { queryKeys } from '@/lib/query-keys'
import * as Sentry from '@sentry/react'
import { redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import type { Capability,MeResponse } from '@/types'

export interface AuthUser {
  id: string
  name?: string
  email?: string
  avatarUrl?: string
  roles?: string[]
  org?: string
  [key: string]: unknown
}

interface StoredAuth {
  accessToken: string
  user: AuthUser
}

const STORAGE_KEY = 'rp_auth' as const
const STORE_KEY = 'selectedStore' as const
const IMPERSONATION_KEY = 'rp_impersonating' as const
const IMPERSONATION_ORG_KEY = 'rp_impersonating_org' as const
// Last org slug a user successfully signed into. Deliberately NOT cleared on
// logout so the login page can skip the org-name step on return. Holds no
// credentials — just the public tenant slug the user already typed.
const LAST_ORG_SLUG_KEY = 'rp_last_org_slug' as const
const EXPIRY_BUFFER_MS = 3 * 60 * 1000

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64)) as Record<string, unknown>
  } catch {
    return null
  }
}

function getTokenExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return null
  return payload.exp * 1000
}

type Listener = () => void

class AuthManager {
  private listeners = new Set<Listener>()

  private read(): StoredAuth | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return null
      return JSON.parse(raw) as StoredAuth
    } catch {
      return null
    }
  }

  private write(data: StoredAuth): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  private clear(): void {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORE_KEY)
    localStorage.removeItem(IMPERSONATION_KEY)
    localStorage.removeItem(IMPERSONATION_ORG_KEY)
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notify(): void {
    this.listeners.forEach((fn) => fn())
  }

  /**
   * Normalizes the login API response and persists the access token + user profile.
   * The refresh token is now stored exclusively in an httpOnly cookie set by the server.
   */
  login(apiResponse: Record<string, unknown>, orgId?: string): void {
    const payload =
      apiResponse.data && typeof apiResponse.data === 'object' && !Array.isArray(apiResponse.data)
        ? (apiResponse.data as Record<string, unknown>)
        : apiResponse

    const accessToken = (payload.accessToken ?? payload.token) as string | undefined

    if (!accessToken) {
      // F-003: do NOT log the response object — it may contain user PII/tokens.
      console.error('[AuthManager] login response missing access token')
      throw new Error('Login response missing access token')
    }

    const jwtPayload = decodeJwtPayload(accessToken)
    const userId = (
      payload.id ?? payload.userId ??
      jwtPayload?.sub ?? jwtPayload?.userId ?? jwtPayload?.id ??
      ''
    ) as string

    const user: AuthUser = {
      id: userId,
      name: (payload.name ?? jwtPayload?.name) as string | undefined,
      email: (payload.email ?? jwtPayload?.email) as string | undefined,
      avatarUrl: payload.avatarUrl as string | undefined,
      roles: payload.roles as string[] | undefined,
      org: orgId ?? (payload.org as string | undefined) ?? (payload.organizationId as string | undefined),
    }

    this.write({ accessToken, user })
    Sentry.setUser({ id: user.id, email: user.email, username: user.name })
    this.notify()
  }

  /** Remember the org slug the user just signed into (survives logout). */
  setLastOrgSlug(slug: string): void {
    const trimmed = slug.trim()
    if (trimmed) localStorage.setItem(LAST_ORG_SLUG_KEY, trimmed)
  }

  /** The last org slug a user signed into on this device, if any. */
  getLastOrgSlug(): string | null {
    try {
      return localStorage.getItem(LAST_ORG_SLUG_KEY)
    } catch {
      return null
    }
  }

  /**
   * Start an admin "act as org" impersonation session from a handoff token
   * minted by the admin portal (POST /admin/organizations/:orgId/impersonate).
   * Decodes the token to build a minimal user; the full profile loads via
   * /auth/me. Returns false if the token is malformed.
   */
  impersonateFromToken(accessToken: string, orgName?: string | null): boolean {
    const jwtPayload = decodeJwtPayload(accessToken)
    if (!jwtPayload || typeof jwtPayload.sub !== 'string') return false
    const user: AuthUser = {
      id: jwtPayload.sub,
      email: jwtPayload.email as string | undefined,
      name: (jwtPayload.name as string | undefined) ?? 'Admin (impersonating)',
      org: jwtPayload.organizationId as string | undefined,
    }
    this.write({ accessToken, user })
    localStorage.setItem(IMPERSONATION_KEY, '1')
    if (orgName) localStorage.setItem(IMPERSONATION_ORG_KEY, orgName)
    Sentry.setUser({ id: user.id, email: user.email, username: user.name })
    this.notify()
    return true
  }

  isImpersonating(): boolean {
    try {
      return localStorage.getItem(IMPERSONATION_KEY) === '1'
    } catch {
      return false
    }
  }

  /** Display name of the impersonated org (set during the handoff), if any. */
  getImpersonationOrgName(): string | null {
    try {
      return localStorage.getItem(IMPERSONATION_ORG_KEY)
    } catch {
      return null
    }
  }

  /**
   * Replaces the stored access token after a refresh. The httpOnly refresh
   * cookie is browser-wide — logging in as another user in any tab replaces
   * it — so a refresh can hand back a token minted for a DIFFERENT account.
   * Adopting it would leave the UI showing user A while every API call runs
   * as user B (a cash-accountability hazard on the POS floor). Returns false
   * on identity mismatch so the caller treats it as a failed refresh.
   */
  setAccessToken(accessToken: string): boolean {
    const current = this.read()
    if (!current) return false
    const sub = decodeJwtPayload(accessToken)?.sub
    if (current.user?.id && typeof sub === 'string' && sub !== current.user.id) {
      return false
    }
    this.write({ ...current, accessToken })
    this.notify()
    return true
  }

  getAccessToken(): string | null {
    return this.read()?.accessToken ?? null
  }

  getUser(): AuthUser | null {
    return this.read()?.user ?? null
  }

  updateUser(partial: Partial<AuthUser>): void {
    const current = this.read()
    if (!current) return
    this.write({ ...current, user: { ...current.user, ...partial } })
    this.notify()
  }

  getOrgId(): string | null {
    return this.read()?.user?.org ?? null
  }

  isAuthenticated(): boolean {
    return !!this.read()?.accessToken
  }

  /**
   * Returns true when the access token will expire within EXPIRY_BUFFER_MS.
   * Used by the request interceptor to proactively refresh before 401s occur.
   */
  isTokenExpiringSoon(): boolean {
    const token = this.getAccessToken()
    if (!token) return false
    const expiryMs = getTokenExpiryMs(token)
    if (expiryMs === null) return false
    return Date.now() >= expiryMs - EXPIRY_BUFFER_MS
  }

  /**
   * Clears local state immediately, then best-effort revokes the server session
   * (which clears the httpOnly refresh cookie). UI consumers are notified synchronously.
   */
  async logout(): Promise<void> {
    // An admin act-as-org token is revoked server-side on exit (it has no
    // refresh cookie, so /auth/logout alone would leave it valid for its hour).
    const impersonationToken = this.isImpersonating() ? this.getAccessToken() : null
    if (impersonationToken) {
      try {
        await fetch(resolveUrl(`/auth/impersonation/end`), {
          method: 'POST',
          headers: { Authorization: `Bearer ${impersonationToken}` },
        })
      } catch {
        // best-effort — the token still expires on its own
      }
    }
    this.clear()
    Sentry.setUser(null)
    this.notify()

    try {
      await fetch(resolveUrl(`/auth/logout`), {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // revocation failed — refresh cookie will expire naturally
    }
  }

  /**
   * Synchronous variant for use in interceptors. Clears local state only —
   * does NOT call the server logout endpoint.
   */
  forceLogout(): void {
    this.clear()
    Sentry.setUser(null)
    this.notify()
  }
}

export const authManager = new AuthManager()

/**
 * Reusable auth guard for TanStack Router `beforeLoad`.
 * 
 * Checks if the user is authenticated. If not, redirects to the login page.
 * 
 * Usage:
 * ```typescript
 * export const Route = createFileRoute('/_dashboard')({
 *   beforeLoad: requireAuth(),
 *   component: DashboardLayout,
 * })
 * ```
 * 
 * Note: Layout routes (like `_dashboard`) automatically protect all child routes.
 * Child routes inherit this protection and do NOT need individual guards.
 */
/** Throw a redirect to the sign-in screen: the remembered org's page
 *  (/auth/:slug) when we know it, else the org-name entry (/). */
function throwLoginRedirect(): never {
  const slug = authManager.getLastOrgSlug()
  throw slug
    ? redirect({ to: '/auth/$slug', params: { slug } })
    : redirect({ to: '/' })
}

export function requireAuth() {
  return () => {
    if (typeof window === 'undefined') return // Skip during SSR

    if (!authManager.isAuthenticated()) {
      throwLoginRedirect()
    }
  }
}

/**
 * Reusable "already authenticated" guard for public pages (login, signup, etc.).
 * 
 * Checks if the user is already authenticated. If yes, redirects to the dashboard.
 * 
 * Usage:
 * ```typescript
 * export const Route = createFileRoute('/')({
 *   beforeLoad: requireGuest('/inventory/stock'),
 *   component: LoginPage,
 * })
 * ```
 */
/**
 * Capability route guard for `beforeLoad`. Requires authentication AND that the
 * signed-in user holds `capability`; otherwise redirects away so the page never
 * renders (closing UI leaks where a management screen is reachable by URL even
 * though the nav entry is hidden). The backend still enforces every capability.
 *
 * Reads `/auth/me` via the router's queryClient (cached), so it works on a cold
 * deep-link too.
 *
 * Usage:
 * ```typescript
 * export const Route = createFileRoute('/_dashboard/catalog/brands')({
 *   beforeLoad: requireCapability('MANAGE_CATALOG'),
 *   component: BrandsLayout,
 * })
 * ```
 */
export function requireCapability(capability: Capability) {
  return async ({ context }: { context: { queryClient: QueryClient } }) => {
    if (typeof window === 'undefined') return // Skip during SSR
    if (!authManager.isAuthenticated()) {
      throwLoginRedirect()
    }
    let me: MeResponse | undefined
    try {
      me = await context.queryClient.ensureQueryData<MeResponse>({
        queryKey: queryKeys.me,
        queryFn: () => api.get<MeResponse>('/auth/me'),
        staleTime: 5 * 60_000,
      })
    } catch {
      throwLoginRedirect() // token invalid / not authenticated
    }
    if (!me?.capabilities?.includes(capability)) {
      throw redirect({ to: '/dashboard' })
    }
  }
}

export function requireGuest(redirectTo: string = '/inventory/stock') {
  return () => {
    if (typeof window === 'undefined') return // Skip during SSR
    
    if (authManager.isAuthenticated()) {
      throw redirect({ to: redirectTo })
    }
  }
}

// One-time migration: drop the stored refreshToken from legacy rp_auth entries.
// The refresh token is now httpOnly-cookie-only and must not live in localStorage.
;(function pruneStoredRefreshToken() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const stored = JSON.parse(raw) as Record<string, unknown>
    if (!('refreshToken' in stored)) return
    const { refreshToken: _drop, ...rest } = stored
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest))
  } catch {
    // safe to ignore
  }
})()
